import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AddTransactionForm } from "../components/AddTransactionForm";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  apiDeleteTransaction,
  apiListAccounts,
  apiListCategories,
  apiListTransactions,
  apiUpdateTransaction,
  type AccountRow,
  type CategoryRow,
  type TransactionRow,
} from "../api";
import { currentMonthISO, monthRange } from "../utils/money";

function useTransactionRowState(
  t: TransactionRow,
  accounts: AccountRow[],
  categories: CategoryRow[],
  onSaved: () => void,
) {
  const [description, setDescription] = useState(t.description);
  const [accountId, setAccountId] = useState(t.account_id);
  const [categoryId, setCategoryId] = useState(t.category_id ?? "");
  const [occurredOn, setOccurredOn] = useState(t.occurred_on);
  const [amount, setAmount] = useState(String(Math.abs(t.amount_cents) / 100));

  useEffect(() => {
    setDescription(t.description);
    setAccountId(t.account_id);
    setCategoryId(t.category_id ?? "");
    setOccurredOn(t.occurred_on);
    setAmount(String(Math.abs(t.amount_cents) / 100));
  }, [t.id, t.description, t.account_id, t.category_id, t.occurred_on, t.amount_cents]);

  async function patch(partial: Parameters<typeof apiUpdateTransaction>[1]) {
    await apiUpdateTransaction(t.id, partial);
    onSaved();
  }

  return {
    description,
    setDescription,
    accountId,
    setAccountId,
    categoryId,
    setCategoryId,
    occurredOn,
    setOccurredOn,
    amount,
    setAmount,
    patch,
  };
}

function TransactionTableRow({
  t,
  accounts,
  categories,
  onSaved,
  onDelete,
}: {
  t: TransactionRow;
  accounts: AccountRow[];
  categories: CategoryRow[];
  onSaved: () => void;
  onDelete: () => void;
}) {
  const row = useTransactionRowState(t, accounts, categories, onSaved);

  return (
    <tr>
      <td className="col-date">
        <input
          type="date"
          className="input-inline"
          value={row.occurredOn}
          onChange={(e) => row.setOccurredOn(e.currentTarget.value)}
          onBlur={() => void row.patch({ occurred_on: row.occurredOn })}
        />
      </td>
      <td className="col-desc">
        <input
          className="input-inline"
          value={row.description}
          onChange={(e) => row.setDescription(e.currentTarget.value)}
          onBlur={() => void row.patch({ description: row.description.trim() })}
        />
      </td>
      <td className="col-cat">
        <select
          className="txn-select"
          value={row.categoryId}
          onChange={(e) => {
            const v = e.currentTarget.value;
            row.setCategoryId(v);
            void row.patch({ category_id: v });
          }}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </td>
      <td className="col-acct">
        <select
          className="txn-select"
          value={row.accountId}
          onChange={(e) => {
            const v = e.currentTarget.value;
            row.setAccountId(v);
            void row.patch({ account_id: v });
          }}
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </td>
      <td className={`right mono col-amt ${t.amount_cents >= 0 ? "amt-in" : "amt-out"}`}>
        <input
          className="input-inline input-amt"
          inputMode="decimal"
          value={row.amount}
          onChange={(e) => row.setAmount(e.currentTarget.value)}
          onBlur={() => {
            const n = Number(row.amount);
            if (Number.isFinite(n) && n > 0) void row.patch({ amount: n });
          }}
        />
      </td>
      <td className="right col-actions">
        <button type="button" className="btn-ghost btn-sm" onClick={onDelete}>
          Delete
        </button>
      </td>
    </tr>
  );
}

function TransactionCard({
  t,
  accounts,
  categories,
  onSaved,
  onDelete,
}: {
  t: TransactionRow;
  accounts: AccountRow[];
  categories: CategoryRow[];
  onSaved: () => void;
  onDelete: () => void;
}) {
  const row = useTransactionRowState(t, accounts, categories, onSaved);

  return (
    <article className="txn-card">
      <div className="txn-card-fields">
        <label>
          Date
          <input
            type="date"
            className="input-inline"
            value={row.occurredOn}
            onChange={(e) => row.setOccurredOn(e.currentTarget.value)}
            onBlur={() => void row.patch({ occurred_on: row.occurredOn })}
          />
        </label>
        <label>
          Description
          <input
            className="input-inline"
            value={row.description}
            onChange={(e) => row.setDescription(e.currentTarget.value)}
            onBlur={() => void row.patch({ description: row.description.trim() })}
          />
        </label>
        <label>
          Category
          <select
            className="txn-select"
            value={row.categoryId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              row.setCategoryId(v);
              void row.patch({ category_id: v });
            }}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Account
          <select
            className="txn-select"
            value={row.accountId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              row.setAccountId(v);
              void row.patch({ account_id: v });
            }}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Amount ({t.amount_cents >= 0 ? "in" : "out"})
          <input
            className={`input-inline input-amt ${t.amount_cents >= 0 ? "amt-in" : "amt-out"}`}
            inputMode="decimal"
            value={row.amount}
            onChange={(e) => row.setAmount(e.currentTarget.value)}
            onBlur={() => {
              const n = Number(row.amount);
              if (Number.isFinite(n) && n > 0) void row.patch({ amount: n });
            }}
          />
        </label>
      </div>
      <div className="txn-card-actions">
        <button type="button" className="btn-ghost btn-sm" onClick={onDelete}>
          Delete
        </button>
      </div>
    </article>
  );
}

export function TransactionsPage() {
  const [month, setMonth] = useState(currentMonthISO);
  const [accountFilter, setAccountFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const compactTxnList = useMediaQuery("(max-width: 640px)");

  const loadAll = useCallback(async () => {
    const { from, to } = monthRange(month);
    const [a, c, txs] = await Promise.all([
      apiListAccounts(),
      apiListCategories(),
      apiListTransactions({
        from,
        to,
        accountId: accountFilter || undefined,
        categoryId: categoryFilter || undefined,
      }),
    ]);
    setAccounts(a.accounts);
    setCategories(c.categories);
    setTransactions(txs.transactions);
  }, [month, accountFilter, categoryFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadAll();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  async function remove(id: string) {
    if (!confirm("Delete this transaction?")) return;
    setError(null);
    try {
      await apiDeleteTransaction(id);
      await loadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
    }
  }

  if (loading && accounts.length === 0) {
    return <p className="muted">Loading…</p>;
  }

  return (
    <div className="stack">
      <div className="card row-between">
        <div>
          <h2 className="mt-0">Transactions</h2>
          <p className="muted mb-0">
            <span className="text-intro-short">
              Positive amounts; category sets the sign. Between-account moves:{" "}
              <Link to="/transfers">Transfers</Link>.
            </span>
            <span className="text-intro-long">
              Log income and expenses on any account (including savings). Enter amounts as positive numbers; category
              sets sign. To move money between accounts without affecting this month&apos;s income/expense totals, use{" "}
              <Link to="/transfers">Transfers</Link>.
            </span>
          </p>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="card">
        <div className="form-row filters">
          <label>
            Month
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.currentTarget.value)}
              className="input-compact"
            />
          </label>
          <label>
            Account filter
            <select value={accountFilter} onChange={(e) => setAccountFilter(e.currentTarget.value)}>
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Category filter
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.currentTarget.value)}>
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <AddTransactionForm accounts={accounts} categories={categories} onCreated={() => void loadAll()} showDate />

      <div className="card">
        {compactTxnList ? (
          <div className="txn-cards">
            {transactions.length === 0 ? (
              <p className="muted mb-0">No transactions in this range.</p>
            ) : (
              transactions.map((t) => (
                <TransactionCard
                  key={t.id}
                  t={t}
                  accounts={accounts}
                  categories={categories}
                  onSaved={() => void loadAll()}
                  onDelete={() => remove(t.id)}
                />
              ))
            )}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table table-txn">
              <thead>
                <tr>
                  <th className="col-date">Date</th>
                  <th className="col-desc">Description</th>
                  <th className="col-cat">Category</th>
                  <th className="col-acct">Account</th>
                  <th className="right col-amt">Amount</th>
                  <th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      No transactions in this range.
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <TransactionTableRow
                      key={t.id}
                      t={t}
                      accounts={accounts}
                      categories={categories}
                      onSaved={() => void loadAll()}
                      onDelete={() => remove(t.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
